import React, { useState } from 'react';
import { SettingsLayout } from '@/components/profile/SettingsLayout';
import { ProfileSettings } from '@/components/profile/ProfileSettings';
import { SecuritySettings } from '@/components/profile/SecuritySettings';
import { NotificationSettings } from '@/components/profile/NotificationSettings';
import { useAuth } from '@/lib/auth/store';
import { User as UserIcon, Lock, Bell, Trash2, Key } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { supabase } from '@/lib/supabase/client';

const countryCodes = [
  { code: '+1', label: 'US/Canada' },
  { code: '+44', label: 'UK' },
  { code: '+61', label: 'Australia' },
  { code: '+91', label: 'India' },
  // ...add more as needed
];

function AccountSettings({ user }: { user: any }) {
  const [email, setEmail] = useState(user.email || '');
  const [phone, setPhone] = useState(user.phone || '');
  const [countryCode, setCountryCode] = useState('+1');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);

  // Leave a comment for future expansion: messaging app handles, WhatsApp, Telegram, etc.
  // e.g. // TODO: Add support for messaging app handles for AI concierge integration

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    setDirty(true);
  };
  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPhone(e.target.value);
    setDirty(true);
  };
  const handleCountryCodeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setCountryCode(e.target.value);
    setDirty(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setSuccess(null);
    setError(null);
    try {
      // Update email if changed
      if (email !== user.email) {
        const { error: emailError } = await supabase.auth.updateUser({ email });
        if (emailError) throw emailError;
      }
      // Update phone if changed
      if (phone !== user.phone || countryCode !== (user.country_code || '+1')) {
        const fullPhone = countryCode + phone;
        const { error: phoneError } = await supabase.auth.updateUser({ phone: fullPhone });
        if (phoneError) throw phoneError;
      }
      setSuccess('Account updated successfully.');
      setDirty(false);
    } catch (err: any) {
      setError(err.message || 'Failed to update account.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form className="space-y-8" onSubmit={handleSubmit}>
      <div>
        <h2 className="text-xl font-bold mb-4">Account Basics</h2>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-sand-300 mb-2">Email Address</label>
            <input
              type="email"
              value={email}
              onChange={handleEmailChange}
              className="w-full px-3 py-2 bg-earth-800 border border-earth-700 text-sand-100 rounded focus:outline-none focus:ring-1 focus:ring-sand-400"
              autoComplete="email"
              disabled={loading}
            />
            <p className="text-xs text-sand-400 mt-1">We'll send important updates to this address.</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-sand-300 mb-2">Phone Number</label>
            <div className="flex gap-2">
              <select
                value={countryCode}
                onChange={handleCountryCodeChange}
                className="px-2 py-2 bg-earth-800 border border-earth-700 text-sand-100 rounded focus:outline-none focus:ring-1 focus:ring-sand-400"
                disabled={loading}
              >
                {countryCodes.map((c) => (
                  <option key={c.code} value={c.code}>{c.code} ({c.label})</option>
                ))}
              </select>
              <input
                type="tel"
                value={phone}
                onChange={handlePhoneChange}
                className="flex-1 px-3 py-2 bg-earth-800 border border-earth-700 text-sand-100 rounded focus:outline-none focus:ring-1 focus:ring-sand-400"
                autoComplete="tel"
                placeholder="Phone number"
                disabled={loading}
              />
            </div>
            <p className="text-xs text-sand-400 mt-1">For booking reminders and account security.</p>
          </div>
        </div>
      </div>
      <div>
        <h2 className="text-xl font-bold mb-4">Password</h2>
        <Button
          type="button"
          variant="outline"
          className="flex items-center gap-2"
          onClick={() => setShowPassword((v) => !v)}
        >
          <Key className="w-4 h-4" />
          Change Password
        </Button>
        {showPassword && (
          <div className="mt-4 bg-dark-300 p-4 rounded-lg">
            <p className="text-sand-400 mb-2">Password change coming soon. Use the Security tab for now.</p>
          </div>
        )}
      </div>
      <div className="pt-6 border-t border-dark-400">
        <h2 className="text-xl font-bold mb-4 text-red-400 flex items-center gap-2">
          <Trash2 className="w-5 h-5" /> Danger Zone
        </h2>
        <p className="text-sand-400 mb-2">Permanently delete your account and all data. This action cannot be undone.</p>
        <Button type="button" variant="outline" className="text-red-500 hover:text-red-400 border-red-500" disabled>
          Delete Account
        </Button>
      </div>
      {(success || error) && (
        <div className={success ? 'text-green-500' : 'text-red-500'}>
          {success || error}
        </div>
      )}
      <div className="flex justify-end">
        <Button type="submit" disabled={loading || !dirty}>
          {loading ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>
    </form>
  );
}

export function Settings() {
  const { user } = useAuth();

  if (!user) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <p className="text-gray-400">Please sign in to access your settings.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 pb-12 bg-dark-100">
      <div className="container mx-auto px-4">
        <h1 className="text-3xl font-bold mb-8">Account Settings</h1>
        <div className="max-w-5xl">
          <SettingsLayout
            user={user}
            sections={[
              {
                key: 'account',
                label: 'Account',
                icon: <UserIcon className="w-5 h-5" />,
                component: <AccountSettings user={user} />,
              },
              {
                key: 'profile',
                label: 'Profile',
                icon: <UserIcon className="w-5 h-5" />,
                component: <ProfileSettings user={user} />,
              },
              {
                key: 'security',
                label: 'Security',
                icon: <Lock className="w-5 h-5" />,
                component: <SecuritySettings />,
              },
              {
                key: 'notifications',
                label: 'Notifications',
                icon: <Bell className="w-5 h-5" />,
                component: <NotificationSettings />,
              },
            ]}
          />
        </div>
      </div>
    </div>
  );
}